import React, { useEffect, useState } from "react";
import Select from "react-select";
import config from "../config";
import "../css/theme.css";
import "../css/components.css";
import "./ManageResorts.css"; // reuses .resorts-page-container/.resorts-table/.vt-page-header - same shared look as PaymentAccounts.jsx/RateManagement.jsx
import "./CollectionsOverview.css";
import { menuPortalTarget, menuPosition, themedSelectStyles } from "../utils/reactSelectTheme";
import ToastContainer, { useToast } from "./common/Toast";

const MONTH_OPTIONS = [
  { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
  { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
  { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
  { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" },
];

const COMMISSION_MODEL_LABELS = {
  GROWTH_RENTED: "Growth Model - Rented",
  GROWTH_REVENUE: "Growth Model - Revenue",
  GROWTH_MARKETING: "Growth Model - Marketing",
  STANDARD_RENTED: "Standard Commission Model - Rented",
  STANDARD_MARKETING: "Standard Commission Model - Marketing",
};

const STATUS_LABELS = {
  LIVE: "Not generated (live estimate)",
  DRAFT: "Draft",
  GENERATED: "Generated",
  APPROVED: "Approved",
  SETTLED: "Settled",
  PENDING: "Pending",
};

const money = (n) => (n === null || n === undefined ? "-" : `₹${Number(n).toLocaleString()}`);

/**
 * Cross-property rollup of this month's turnover/collection/commission/
 * payout - one row per resort the current user can see (SUPER_ADMIN gets
 * every active resort; everyone else - including a PROPERTY_MANAGER-scoped
 * B2B partner - gets just their assigned resort(s), same scoping every
 * other admin page already uses). All figures come straight from
 * SettlementService.getCollectionsOverview - no calculation happens here,
 * this is purely a read-only table over the same numbers
 * MonthlySettlement.jsx already computes one resort at a time.
 */
const CollectionsOverview = () => {
  const { toasts, showToast, dismissToast } = useToast();

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${config.BASE_URL}/api/settlement/overview?year=${selectedYear}&month=${selectedMonth}`, {
      headers: config.getHeaders(),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load collections overview");
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((err) => {
        if (!cancelled) showToast(err.message, "danger");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear]);

  const sum = (key) => rows.reduce((total, r) => total + (r[key] != null ? Number(r[key]) : 0), 0);
  const hasOwnerPayout = rows.some((r) => r.ownerSettlementAmount !== null && r.ownerSettlementAmount !== undefined);

  return (
    <div className="resorts-page-container collections-overview-page" style={{ padding: "0px 20px 20px 20px", boxSizing: "border-box" }}>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      <div className="vt-page-header">
        <h2>Collections Overview</h2>
      </div>

      <div className="collections-overview-filters">
        <div className="filter-item">
          <label>Month</label>
          <Select
            classNamePrefix="react-select"
            options={MONTH_OPTIONS}
            value={MONTH_OPTIONS.find((m) => m.value === selectedMonth)}
            onChange={(obj) => setSelectedMonth(obj.value)}
            menuPortalTarget={menuPortalTarget}
            menuPosition={menuPosition}
            styles={themedSelectStyles()}
          />
        </div>
        <div className="filter-item filter-item--narrow">
          <label>Year</label>
          <input
            type="number"
            className="year-input"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          />
        </div>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : rows.length === 0 ? (
        <p>No resorts to show.</p>
      ) : (
        <>
          <div className="collections-overview-summary">
            <div className="collections-overview-card">
              <div className="collections-overview-card-label">Total Turnover</div>
              <div className="collections-overview-card-value">{money(sum("resortTurnover"))}</div>
            </div>
            <div className="collections-overview-card">
              <div className="collections-overview-card-label">Collected via Vintara</div>
              <div className="collections-overview-card-value">{money(sum("vintaraCollection"))}</div>
            </div>
            <div className="collections-overview-card">
              <div className="collections-overview-card-label">Collected by Property</div>
              <div className="collections-overview-card-value">{money(sum("propertyCollection"))}</div>
            </div>
            {hasOwnerPayout && (
              <div className="collections-overview-card">
                <div className="collections-overview-card-label">Owner Payout Due</div>
                <div className="collections-overview-card-value">{money(sum("ownerSettlementAmount"))}</div>
              </div>
            )}
          </div>

          <div className="table-wrapper">
            <table className="resorts-table">
              <thead>
                <tr>
                  <th>Resort</th>
                  <th>Commission Model</th>
                  <th>Status</th>
                  <th>Turnover</th>
                  <th>Vintara Collection</th>
                  <th>Property Collection</th>
                  <th>Commission</th>
                  <th>Owner Payout</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.resortId}>
                    <td>{r.resortName}</td>
                    <td>{COMMISSION_MODEL_LABELS[r.commissionModel] || r.commissionModel || "-"}</td>
                    <td>
                      <span className={`collections-overview-status collections-overview-status--${r.status?.toLowerCase()}`}>
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                    </td>
                    <td>{money(r.resortTurnover)}</td>
                    <td>{money(r.vintaraCollection)}</td>
                    <td>{money(r.propertyCollection)}</td>
                    <td>{money(r.totalMarketingCommission)}</td>
                    <td>{r.ownerSettlementAmount != null ? money(r.ownerSettlementAmount) : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default CollectionsOverview;
